import React, { Component, ErrorInfo, ReactNode } from 'react';
import styled from 'styled-components';

// Styled Components para el Error Boundary
const ErrorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
  padding: 20px;
  background: #fff;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  margin: 20px;
`;

const ErrorTitle = styled.h2`
  color: #ef4444;
  margin: 0 0 16px 0;
  font-size: 18px;
`;

const ErrorMessage = styled.p`
  color: #666;
  text-align: center;
  margin: 0 0 16px 0;
  line-height: 1.5;
`;

const ErrorDetails = styled.details`
  width: 100%;
  max-width: 600px;
  margin-top: 16px;
  
  summary {
    cursor: pointer;
    color: #0066cc;
    font-weight: 500;
    margin-bottom: 8px;
  }
  
  pre {
    background: #f8f9fa;
    padding: 12px;
    border-radius: 4px;
    overflow-x: auto;
    font-size: 12px;
    color: #333;
    white-space: pre-wrap;
  }
`;

const RetryButton = styled.button`
  padding: 8px 16px;
  background: #0066cc;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;
  
  &:hover {
    background: #0052a3;
  }
`;

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: ErrorInfo) => void;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    // Actualizar el estado para mostrar la UI de error
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Registrar el error
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Llamar al callback de error si se proporciona
    if (this.props.onError) {
      this.props.onError(error, errorInfo);
    }
  }

  handleRetry = () => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render() {
    if (this.state.hasError) {
      // Si se proporciona un fallback personalizado, usarlo
      if (this.props.fallback) {
        return this.props.fallback;
      }

      // UI de error por defecto
      return (
        <ErrorContainer>
          <ErrorTitle>🚨 Algo salió mal</ErrorTitle>
          <ErrorMessage>
            Ha ocurrido un error inesperado en esta parte de la aplicación.
            <br />
            Puedes intentar recargar la página o contactar al soporte técnico.
          </ErrorMessage>
          
          <RetryButton onClick={this.handleRetry}>
            Intentar de nuevo
          </RetryButton>

          {process.env.NODE_ENV === 'development' && this.state.error && (
            <ErrorDetails>
              <summary>Detalles técnicos (solo en desarrollo)</summary>
              <pre>
                <strong>Error:</strong> {this.state.error.message}
                {'\n\n'}
                <strong>Stack:</strong> {this.state.error.stack}
                {this.state.errorInfo && (
                  <>
                    {'\n\n'}
                    <strong>Component Stack:</strong> {this.state.errorInfo.componentStack}
                  </>
                )}
              </pre>
            </ErrorDetails>
          )}
        </ErrorContainer>
      );
    }

    return this.props.children;
  }
}

// Hook para usar el Error Boundary de forma más sencilla
export const withErrorBoundary = <P extends object>(
  Component: React.ComponentType<P>,
  fallback?: ReactNode
) => {
  return (props: P) => (
    <ErrorBoundary fallback={fallback}>
      <Component {...props} />
    </ErrorBoundary>
  );
};

// Componente de Error Boundary específico para Zustand
export const ZustandErrorBoundary: React.FC<{ children: ReactNode }> = ({ children }) => {
  const handleError = (error: Error, errorInfo: ErrorInfo) => {
    // Log específico para errores de Zustand
    console.error('Zustand Store Error:', {
      error: error.message,
      stack: error.stack,
      componentStack: errorInfo.componentStack,
      timestamp: new Date().toISOString(),
    });
  };

  return (
    <ErrorBoundary 
      onError={handleError}
      fallback={
        <ErrorContainer>
          <ErrorTitle>⚠️ Error en el Estado</ErrorTitle>
          <ErrorMessage>
            Ha ocurrido un error en el manejo del estado de la aplicación.
            <br />
            Los datos pueden no estar actualizados correctamente.
          </ErrorMessage>
          <RetryButton onClick={() => window.location.reload()}>
            Recargar Aplicación
          </RetryButton>
        </ErrorContainer>
      }
    >
      {children}
    </ErrorBoundary>
  );
};